import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";

export default function PaymentResult({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(mode === "cancel" ? "cancel" : "checking");
  const [kind, setKind] = useState(null);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (mode === "cancel") return;
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.kind) setKind(res.data.kind);
        if (res.data.payment_status === "paid") { setStatus("paid"); return; }
        if (["expired", "failed"].includes(res.data.payment_status)) { setStatus("error"); return; }
      } catch (e) { /* retry */ }
      if (attempts >= 10) { setStatus("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [mode, sessionId]);

  const isRent = kind === "rent";
  const isPlan = kind === "plan_upgrade";
  const isEdl = kind === "edl_addon";
  const config = {
    checking: { icon: Loader2, spin: true, title: "Vérification du paiement...", desc: "Merci de patienter quelques secondes.", color: "text-brand-greenDeep" },
    paid: {
      icon: CheckCircle2,
      title: "Paiement réussi !",
      desc: isRent
        ? "Votre loyer a bien été réglé. La quittance apparaît comme payée dans votre espace."
        : isPlan
        ? "Votre abonnement est activé. Vous profitez désormais de toutes les fonctionnalités de votre offre."
        : isEdl
        ? "Votre abonnement « État des lieux » est actif. Vous pouvez dès maintenant créer vos états des lieux."
        : "Votre assurance est souscrite et validée. Vous la retrouverez dans « Mes assurances ».",
      color: "text-brand-limeDark",
    },
    cancel: { icon: XCircle, title: "Paiement annulé", desc: "Aucun montant n'a été prélevé. Vous pouvez réessayer quand vous le souhaitez.", color: "text-orange-500" },
    error: { icon: XCircle, title: "Un souci est survenu", desc: "Le paiement n'a pas pu être confirmé. Réessayez depuis votre espace.", color: "text-red-500" },
  }[status];

  const Icon = config.icon;
  const primaryTo = isRent ? "/app/mon-espace" : isPlan ? "/app/profil" : isEdl ? "/app/etats-des-lieux" : "/app/mes-assurances";
  const primaryLabel = isRent ? "Retour à mon espace" : isPlan ? "Voir mon abonnement" : isEdl ? "Accéder aux états des lieux" : "Voir mes assurances";

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-softer px-5">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center border border-brand-soft">
        <Icon className={`w-16 h-16 mx-auto mb-5 ${config.color} ${config.spin ? "animate-spin" : ""}`} />
        <h1 className="font-display text-3xl font-semibold text-brand-greenDeep">{config.title}</h1>
        <p className="text-brand-green/60 mt-3">{config.desc}</p>
        {status !== "checking" && (
          <button onClick={() => navigate(primaryTo)} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors">
            {primaryLabel} <ArrowRight className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => navigate("/app")} className="mt-3 block w-full text-sm text-brand-green/50 hover:text-brand-green">Retour au tableau de bord</button>
      </div>
    </div>
  );
}
